import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { PanelCard, StatHeader } from './PanelCard';
import { RingGauge, LegendDot } from './Ring';
import { usePanelTokens } from './tokens';

export interface UsageRingCardProps {
  title: ReactNode;
  info?: string;
  /** 0–100 shown in the ring center. */
  percent: number;
  /** Header values. */
  usedValue: ReactNode;
  usedLabel: ReactNode;
  totalValue?: ReactNode;
  totalLabel?: ReactNode;
  /** Ring + accent color. Defaults to brand accent (tokens.a). */
  color?: string;
  /** Center caption under the % (e.g. "Used", "Filled"). */
  centerCaption?: ReactNode;
  /** Legend labels for used / remaining. */
  usedLegend?: ReactNode;
  remainingLegend?: ReactNode;
}

/** Donut usage ring + two-value header + dot legend. */
export function UsageRingCard({
  title,
  info,
  percent,
  usedValue,
  usedLabel,
  totalValue,
  totalLabel,
  color,
  centerCaption = 'Used',
  usedLegend = 'Used',
  remainingLegend = 'Remaining',
}: UsageRingCardProps) {
  const t = usePanelTokens();
  const arc = color ?? t.a;
  return (
    <PanelCard title={title} info={info}>
      <StatHeader
        usedValue={usedValue}
        usedLabel={usedLabel}
        totalValue={totalValue}
        totalLabel={totalLabel}
        usedColor={arc}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mt: 2 }}>
        <RingGauge
          value={percent}
          color={arc}
          centerTop={`${Math.round(percent)}%`}
          centerBottom={centerCaption}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
          <LegendDot color={arc}>{usedLegend}</LegendDot>
          <LegendDot color={t.aSoft}>{remainingLegend}</LegendDot>
        </Box>
      </Box>
    </PanelCard>
  );
}
